class BuildYourBoardModel extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
  }

  connectedCallback() {
    // Prevent multiple initializations
    if (this.isInitialized) return;
    this.init();
  }

  init() {
    const container = this.querySelector('#canvas-container');

    // Resolve finish inputs in priority order:
    // 1. Finish option (swatch), 2. Style option (swatch or radio), 3. Oil option (swatch or radio)
    // Ultimate fallback: all swatch inputs on the page.
    const productPage = this.closest('.product-page');

    const resolveInputs = (label) => {
      const wrapper = productPage?.querySelector(`[data-select-label="${label}" i]`);
      if (!wrapper) return null;
      const swatches = wrapper.querySelectorAll('[swatch-option-selector]');
      if (swatches.length) return swatches;
      const radios = wrapper.querySelectorAll('input[type="radio"]');
      return radios.length ? radios : null;
    };

    let swatchInputs =
      resolveInputs('Finish') ||
      resolveInputs('Style') ||
      resolveInputs('Oil') ||
      productPage?.querySelectorAll('[swatch-option-selector]');
    if (!container) return;

    // Mark as initialized to prevent duplicate init calls
    this.isInitialized = true;

    this.createLoader();
    this.showLoader('Loading 3D model...');

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf5f5f5);

    this.camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      100,
      15000
    );

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enablePan = false;
    this.controls.minDistance = 1200;
    this.controls.maxDistance = 6000;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight.position.set(1000, 1500, 1000);
    this.scene.add(directionalLight);

    if (this.dataset.wall === 'true') {
      this.createWall();
    } else {
      setTimeout(() => this.hideLoader(), 100);
    }
    this.createBoard();
    this.positionCamera();

    // Ensure board fits view on mobile initially
    if (this.isMobile()) {
      this.fitCameraToObject(this.board, 1.6);
    }

    // Bind event handlers to maintain 'this' context for proper removal
    this.boundOnResize = this.onResize.bind(this);
    this.boundOnLengthChange = this.onLengthChange.bind(this);
    this.boundOnWidthChange = this.onWidthChange.bind(this);
    this.boundSwatchChange = this.handleSwatchChange.bind(this);

    window.addEventListener('resize', this.boundOnResize);
    document.addEventListener('length-changed', this.boundOnLengthChange);
    document.addEventListener('width-prime-changed', this.boundOnWidthChange);
    // Use capture phase to catch changes before they bubble
    document.addEventListener('change', this.boundSwatchChange, true);

    // Shelf kit bracket support
    if (this.dataset.shelfKit === 'true') {
      this.brackets = [];
      this.bracketColor = 0x333333;
      this.bracketsVisible = false;
      this.bracketType = this.dataset.bracketType || 'bracket';
      this.boundOnAddonUpdate = this.onAddonUpdate.bind(this);
      document.addEventListener('addon:update-gallery', this.boundOnAddonUpdate);

      // Listen for bracket type changes (clicks on bracket-item links)
      this.boundOnBracketTypeClick = this.onBracketTypeClick.bind(this);
      document.querySelectorAll('[data-bracket-item]').forEach(item => {
        // item.addEventListener('click', this.boundOnBracketTypeClick);
      });

      // Sync with existing addon toggle state on load — always show brackets
      // Use a short delay to allow addon-bracket-selector to initialize and check the first radio
      this.addBrackets();
      setTimeout(() => {
        const checkedColor = document.querySelector('[data-addon-input][data-color-code]:checked');
        const colorCode = checkedColor?.getAttribute('data-color-code');
        if (colorCode) {
          this.bracketColor = new THREE.Color(colorCode);
          this.setBracketColor(this.bracketColor);
        }
      }, 100);
    }

    // Use event delegation for swatch changes (works even if DOM is replaced)
    this.boundSwatchChange = this.handleSwatchChange.bind(this);
    document.addEventListener('change', this.boundSwatchChange, true);

    // Listen for when variants block is replaced by width-prime-range
    this.boundOnVariantsBlockUpdated = this.onVariantsBlockUpdated.bind(this);
    document.addEventListener('variants-block:updated', this.boundOnVariantsBlockUpdated);

    // Also handle mutation of variants block to reinitialize listeners
    this.initializeSwatchesOnCurrentDOM();
    this.setupVariantsBlockObserver();

    this.animate();
  }

  handleSwatchChange(event) {
    const target = event.target;
    
    // Check if it's a swatch input by looking for swatch-option-selector attribute or data attributes
    if (!target.matches('[swatch-option-selector], [data-image], [data-color]')) {
      return;
    }

    // Make sure this swatch is actually checked (for radio buttons)
    if (target.type === 'radio' && !target.checked) {
      return;
    }

    // Only proceed if the board exists (fully initialized)
    if (!this.board) {
      return;
    }

    const value = target.getAttribute('data-image') || target.getAttribute('data-color');
    if (value) {
      this.setBoardFinish(value);
      if (this.dataset.shelfKit === 'true') {
        this.setBracketColor(value);
      }
    }
  }

  initializeSwatchesOnCurrentDOM() {
    // Initialize checked swatches only when board is ready
    // Only called on page load and when variants block is explicitly replaced
    if (!this.board) {
      return;
    }

    const swatchInputs = document.querySelectorAll('[swatch-option-selector], [data-image], [data-color]');
    swatchInputs.forEach(input => {
      if (input.checked) {
        const value = input.getAttribute('data-image') || input.getAttribute('data-color');
        if (value) {
          this.setBoardFinish(value);
          if (this.dataset.shelfKit === 'true') {
            this.setBracketColor(value);
          }
        }
      }
    });
  }

  setupVariantsBlockObserver() {
    // We rely on the 'variants-block:updated' event instead of mutation observer
    // This prevents false positives from hover states and other minor DOM changes
    // Removed aggressive mutation observer to prevent loading on hover
  }

  onVariantsBlockUpdated(event) {
    // Called ONLY when the variants block is actually replaced
    // This prevents unnecessary re-initialization from hover effects
    this.initializeSwatchesOnCurrentDOM();
  }

  getThicknessMm() {
    return parseFloat(this.dataset.thickness || '38');
  }

  getWidthMm() {
    return parseFloat(this.dataset.width || '225');
  }

  getLengthCm() {
    const slider = document.querySelector('option-length-range [data-option-range-slider]');
    if (slider) {
      const valueCm = parseFloat(slider.value);
      if (!isNaN(valueCm)) return valueCm;
    }

    const selector = document.querySelector('option-length-range [data-selected-value]');
    if (selector) {
      const valueCm = parseFloat(selector.textContent.trim());
      if (!isNaN(valueCm)) return valueCm;
    }

    return 100;
  }

  getLengthMm() {
    return this.getLengthCm() * 10;
  }

  createLoader() {
    const loader = document.createElement('div');
    loader.className = 'byb-loader';
    loader.innerHTML = `
      <div class="byb-loader__spinner"></div>
      <div class="byb-loader__text">Loading...</div>
    `;
    this.appendChild(loader);
    this.loader = loader;
  }

  showLoader(text = 'Loading...') {
    if (this.loader) {
      this.loader.querySelector('.byb-loader__text').textContent = text;
      this.loader.classList.add('active');
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('active');
    }
  }

  createWall() {
    const wallWidth = 5250;
    const wallHeight = 3000;
    const wallDepth = 60;

    const textureLoader = new THREE.TextureLoader();
    let loadedCount = 0;
    const totalTextures = 4;

    const onTextureLoad = () => {
      loadedCount++;
      if (loadedCount === totalTextures) {
        setTimeout(() => this.hideLoader(), 100);
      }
    };

    const onTextureError = (error) => {
      console.error('Texture loading error:', error);
      loadedCount++;
      if (loadedCount === totalTextures) {
        setTimeout(() => this.hideLoader(), 100);
      }
    };

    const textures = {
      diffuse: textureLoader.load('https://cdn.shopify.com/s/files/1/0438/0904/6680/files/brick_wall_09_diff_1k.jpg?v=1765805542', onTextureLoad, undefined, onTextureError),
      ao: textureLoader.load('https://cdn.shopify.com/s/files/1/0438/0904/6680/files/brick_wall_09_ao_1k.jpg?v=1765805541', onTextureLoad, undefined, onTextureError),
      normal: textureLoader.load('https://cdn.shopify.com/s/files/1/0438/0904/6680/files/brick_wall_09_nor_gl_1k.exr?v=1765805541', onTextureLoad, undefined, onTextureError),
      displacement: textureLoader.load('https://cdn.shopify.com/s/files/1/0438/0904/6680/files/brick_wall_09_disp_1k.png?v=1765805542', onTextureLoad, undefined, onTextureError)
    };

    Object.values(textures).forEach(tex => {
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(6, 3.75);
    });

    const brickMaterial = new THREE.MeshStandardMaterial({
      map: textures.diffuse,
      aoMap: textures.ao,
      normalMap: textures.normal,
      normalScale: new THREE.Vector2(0.8, 0.8),
      displacementMap: textures.displacement,
      displacementScale: 0.08,
      roughness: 0.9,
      metalness: 0
    });

    const edgeMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B4513,
      roughness: 0.9,
      metalness: 0
    });

    const materials = [
      edgeMaterial,
      edgeMaterial,
      edgeMaterial,
      edgeMaterial,
      brickMaterial,
      edgeMaterial
    ];

    const wallGeometry = new THREE.BoxGeometry(wallWidth, wallHeight, wallDepth);
    this.wall = new THREE.Mesh(wallGeometry, materials);
    this.wall.position.set(0, wallHeight / 2, 0);
    this.scene.add(this.wall);

    const coverPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(wallWidth + 4, wallHeight + 4),
      brickMaterial.clone()
    );
    coverPlane.position.set(0, wallHeight / 2, wallDepth / 2 + 1);
    this.scene.add(coverPlane);
  }

  createBoard() {
    const lengthMm = this.getLengthMm();
    const widthMm = this.getWidthMm();
    const thicknessMm = this.getThicknessMm();

    const geometry = new THREE.BoxGeometry(lengthMm, thicknessMm, widthMm);
    const material = new THREE.MeshStandardMaterial({
      color: 0xc4a574,
      roughness: 0.7,
      metalness: 0.1
    });

    this.board = new THREE.Mesh(geometry, material);

    const hasWall = this.dataset.wall === 'true';
    const wallDepth = 60;
    const boardZ = hasWall ? wallDepth / 2 + widthMm / 2 + 5 : 0;
    const boardY = hasWall ? 1200 : 0;

    this.board.position.set(0, boardY, boardZ);
    this.scene.add(this.board);

    this.controls.target.set(0, boardY, boardZ);
    this.controls.update();
  }

  positionCamera() {
    const lengthMm = this.board.geometry.parameters.width;
    const widthMm = this.board.geometry.parameters.depth;
    const hasWall = this.dataset.wall === 'true';

    const maxDimension = Math.max(lengthMm, widthMm);
    let distance = hasWall ? maxDimension * 1.8 + 800 : maxDimension * 1.5 + 500;
    if (hasWall) distance = distance * 0.92;

    distance = Math.max(distance, hasWall ? 1500 : 1200);
    distance = Math.min(distance, hasWall ? 5500 : 4000);

    if (hasWall) {
      const angleVertical = Math.PI / 6;
      const angleHorizontal = Math.PI / 4.5;

      this.camera.position.set(
        Math.cos(angleHorizontal) * distance,
        Math.sin(angleVertical) * distance + 600,
        Math.sin(angleHorizontal) * distance + 400
      );
    } else {
      this.camera.position.set(
        0,
        distance * 0.6,
        distance * 0.8
      );
    }

    this.camera.lookAt(this.controls.target);
    this.controls.update();

    // On mobile, ensure fully in view after initial positioning
    if (this.isMobile()) {
      this.fitCameraToObject(this.board, 1.6);
    }
  }

  onLengthChange(event) {
    const newLengthCm = parseFloat(event.detail.value);
    if (isNaN(newLengthCm) || !this.board) return;

    const newLengthMm = newLengthCm * 10;
    this.animateToLength(newLengthMm);
  }

  onWidthChange(event) {
    const newWidthMm = parseFloat(event.detail.value);
    if (isNaN(newWidthMm) || !this.board) return;

    this.animateToWidth(newWidthMm);
  }

  animateToLength(targetLengthMm) {
    const startLength = this.board.geometry.parameters.width;
    const thicknessMm = this.board.geometry.parameters.height;
    const widthMm = this.board.geometry.parameters.depth;

    const duration = 800;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const currentLength = startLength + (targetLengthMm - startLength) * eased;

      this.board.geometry.dispose();
      this.board.geometry = new THREE.BoxGeometry(currentLength, thicknessMm, widthMm);

      this.updateCamera(currentLength, widthMm);

      // On mobile, keep the board fully visible during animation
      if (this.isMobile()) {
        this.fitCameraToObject(this.board, 1.5);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else if (this.bracketsVisible) {
        this.addBrackets();
      }
    };

    requestAnimationFrame(animate);
  }

  animateToWidth(targetWidthMm) {
    const lengthMm = this.board.geometry.parameters.width;
    const thicknessMm = this.board.geometry.parameters.height;
    const startWidth = this.board.geometry.parameters.depth;

    const duration = 800;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const currentWidth = startWidth + (targetWidthMm - startWidth) * eased;

      this.board.geometry.dispose();
      this.board.geometry = new THREE.BoxGeometry(lengthMm, thicknessMm, currentWidth);

      this.updateCamera(lengthMm, currentWidth);

      // On mobile, keep the board fully visible during animation
      if (this.isMobile()) {
        this.fitCameraToObject(this.board, 1.5);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else if (this.bracketsVisible) {
        this.addBrackets();
      }
    };

    requestAnimationFrame(animate);
  }

  updateCamera(lengthMm, widthMm) {
    const hasWall = this.dataset.wall === 'true';
    const maxDimension = Math.max(lengthMm, widthMm);
    let targetDistance = hasWall ? maxDimension * 1.3 + 600 : maxDimension * 0.8 + 400;

    targetDistance = Math.max(targetDistance, hasWall ? 1500 : 1200);
    targetDistance = Math.min(targetDistance, hasWall ? 5500 : 3500);

    let targetX, targetY, targetZ;

    if (hasWall) {
      const angleVertical = Math.PI / 6;
      const angleHorizontal = Math.PI / 4.5;

      targetX = Math.cos(angleHorizontal) * targetDistance;
      targetY = Math.sin(angleVertical) * targetDistance + 600;
      targetZ = Math.sin(angleHorizontal) * targetDistance + 400;
    } else {
      targetX = 0;
      targetY = targetDistance * 0.6;
      targetZ = targetDistance * 0.8;
    }

    this.camera.position.x += (targetX - this.camera.position.x) * 0.08;
    this.camera.position.y += (targetY - this.camera.position.y) * 0.08;
    this.camera.position.z += (targetZ - this.camera.position.z) * 0.08;

    // On mobile, override with fit-to-object to guarantee visibility
    if (this.isMobile()) {
      this.fitCameraToObject(this.board, 1.5);
    }

    this.controls.update();
  }

  setBoardFinish(value) {
    if (!this.board) return;

    if (value.charAt(0) === '#') {
      this.board.material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(value),
        roughness: 0.7,
        metalness: 0.1
      });
    } else {
      this.showLoader('Loading texture...');
      new THREE.TextureLoader().load(value, (texture) => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        texture.rotation = Math.PI / 2;
        texture.center.set(0.5, 0.5);
        texture.repeat.set(2, 1);
        this.board.material = new THREE.MeshStandardMaterial({
          map: texture,
          roughness: 0.75,
          metalness: 0.05
        });
        this.hideLoader();
      });
    }
  }

  // --- Shelf bracket 3D methods ---

  onAddonUpdate(event) {
    const detail = event.detail;
    if (!detail) return;

    // Always show brackets — when toggle is off, first option is pre-selected;
    // when toggle is on, use whatever the user picked.
    const checkedColor = document.querySelector('[data-addon-input][data-color-code]:checked');
    const colorCode = checkedColor?.getAttribute('data-color-code');
    if (colorCode) {
      this.bracketColor = new THREE.Color(colorCode);
    }

    if (this.bracketsVisible) {
      this.setBracketColor(this.bracketColor);
    } else {
      this.addBrackets();
    }
  }

  onBracketTypeClick(event) {
    const item = event.currentTarget;
    const newType = item.getAttribute('data-value');
    if (!newType || newType === this.bracketType) return;

    this.bracketType = newType;
    if (this.bracketsVisible) {
      this.addBrackets();
    }
  }

  getBracketMaterial() {
    return new THREE.MeshStandardMaterial({
      color: this.bracketColor,
      roughness: 0.65,
      metalness: 0.45
    });
  }

  // --- Type: "bracket" — Standard L-bracket with lip ---
  createBracketStandard(dims) {
    const { metalThickness, metalWidth, verticalHeight, armLength, lipHeight } = dims;
    const material = this.getBracketMaterial();
    const group = new THREE.Group();
    const gap = 1; // small clearance to prevent clipping with the board

    // Vertical arm (flat bar against wall)
    const vertGeo = new THREE.BoxGeometry(metalWidth, verticalHeight, metalThickness);
    const vertMesh = new THREE.Mesh(vertGeo, material);
    vertMesh.position.set(0, verticalHeight / 2, metalThickness / 2);
    group.add(vertMesh);

    // Horizontal arm (under the shelf)
    const horizLen = armLength - metalThickness;
    const horizGeo = new THREE.BoxGeometry(metalWidth, metalThickness, horizLen);
    const horizMesh = new THREE.Mesh(horizGeo, material.clone());
    horizMesh.position.set(0, metalThickness / 2 - gap, metalThickness + horizLen / 2);
    group.add(horizMesh);

    // Front lip (sits outside the board front face, extends upward)
    if (lipHeight > 0) {
      const lipGeo = new THREE.BoxGeometry(metalWidth, lipHeight, metalThickness);
      const lipMesh = new THREE.Mesh(lipGeo, material.clone());
      lipMesh.position.set(0, metalThickness - gap + lipHeight / 2, armLength + metalThickness / 2);
      group.add(lipMesh);
    }

    return group;
  }

  // --- Type: "propped" — L-bracket that props shelf from underneath ---
  // Horizontal arm under the shelf, vertical arm extends downward against the wall.
  // Curved inner corner where the arms meet. Front lip sits outside the board face.
  createBracketPropped(dims) {
    const { metalThickness, metalWidth, verticalHeight, armLength, lipHeight } = dims;
    const material = this.getBracketMaterial();
    const group = new THREE.Group();
    const gap = 1;
    const r = metalThickness * 2.5; // curve radius for rounded inner corner

    // 2D cross-section: horizontal arm on top, vertical arm going down, curved inside corner
    const shape = new THREE.Shape();

    // Start top-left corner (back wall side, top of horizontal arm)
    shape.moveTo(0, metalThickness - gap);
    // Along top of horizontal arm to front end
    shape.lineTo(armLength, metalThickness - gap);
    // Down front edge of horizontal arm
    shape.lineTo(armLength, -gap);
    // Back along bottom of horizontal arm toward inner corner
    shape.lineTo(metalThickness + r, -gap);
    // Curved inner corner (quarter arc going into vertical arm)
    shape.absarc(
      metalThickness + r,  // center Z
      -gap - r,            // center Y
      r,                   // radius
      Math.PI / 2,         // start angle (top)
      Math.PI,             // end angle (left)
      false                // counter-clockwise
    );
    // Down the inner face of vertical arm
    shape.lineTo(metalThickness, -gap - verticalHeight);
    // Across bottom of vertical arm
    shape.lineTo(0, -gap - verticalHeight);
    // Back up outer face (wall side) to start
    shape.lineTo(0, metalThickness - gap);

    const geometry = new THREE.ExtrudeGeometry(shape, {
      steps: 1,
      depth: metalWidth,
      bevelEnabled: false
    });
    geometry.translate(0, 0, -metalWidth / 2);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.y = -Math.PI / 2;
    group.add(mesh);

    // Front lip (sits outside the board front face, extends upward)
    if (lipHeight > 0) {
      const lipGeo = new THREE.BoxGeometry(metalWidth, lipHeight, metalThickness);
      const lipMesh = new THREE.Mesh(lipGeo, material.clone());
      lipMesh.position.set(0, metalThickness - gap + lipHeight / 2, armLength + metalThickness / 2);
      group.add(lipMesh);
    }

    return group;
  }

  // --- Type: "floating" — Hidden bracket (rod inserted into board) ---
  createBracketFloating(dims) {
    const { metalThickness, metalWidth, armLength, boardThickness } = dims;
    const material = this.getBracketMaterial();
    const group = new THREE.Group();

    // Back plate (flush against wall, short and flat)
    const plateHeight = boardThickness * 2.5;
    const plateWidth = metalWidth * 2;
    const plateGeo = new THREE.BoxGeometry(plateWidth, plateHeight, metalThickness);
    const plateMesh = new THREE.Mesh(plateGeo, material);
    plateMesh.position.set(0, plateHeight / 2 - boardThickness * 0.5, metalThickness / 2);
    group.add(plateMesh);

    // Two horizontal rods that insert into the board
    const rodDiameter = metalThickness * 1.5;
    const rodLength = armLength * 0.75;
    const rodGeo = new THREE.CylinderGeometry(rodDiameter / 2, rodDiameter / 2, rodLength, 8);
    const rodSpacing = metalWidth * 0.45;

    for (let r = 0; r < 2; r++) {
      const rodMesh = new THREE.Mesh(rodGeo, material.clone());
      rodMesh.rotation.x = Math.PI / 2;
      const xOff = r === 0 ? -rodSpacing : rodSpacing;
      rodMesh.position.set(xOff, 0, metalThickness + rodLength / 2);
      group.add(rodMesh);
    }

    return group;
  }

  createBracketGroup(dims) {
    switch (this.bracketType) {
      case 'hanging':
      case 'bracket':  return this.createBracketStandard(dims);
      case 'propped':  return this.createBracketPropped(dims);
      case 'floating': return this.createBracketFloating(dims);
      default:         return this.createBracketStandard(dims);
    }
  }

  // Convert length in mm to feet
  lengthMmToFeet(mm) {
    return mm / 304.8;
  }

  // Bracket count based on board length in cm:
  // 30–149cm  → 2 brackets
  // 150–299cm → 3 brackets
  // 300cm+    → 4 brackets
  getBracketCount(lengthMm) {
    const lengthCm = lengthMm / 10;
    if (lengthCm <= 149) return 2;
    if (lengthCm <= 299) return 3;
    return 4;
  }

  addBrackets() {
    this.removeBrackets();
    if (!this.board) return;

    const lengthMm  = this.board.geometry.parameters.width;
    const thickMm   = this.board.geometry.parameters.height;
    const widthMm   = this.board.geometry.parameters.depth;

    const boardY = this.board.position.y;
    const boardZ = this.board.position.z;
    const hasWall = this.dataset.wall === 'true';
    const wallDepth = 60;

    const boardBottom = boardY - thickMm / 2;
    const boardTop    = boardY + thickMm / 2;
    const boardBack   = boardZ - widthMm / 2;
    const boardFront  = boardZ + widthMm / 2;
    const wallFace    = hasWall ? wallDepth / 2 : boardBack;

    const metalThickness = 5;
    const metalWidth = 25;
    const armLength = boardFront - wallFace;

    const isFloating = this.bracketType === 'floating';
    const isPropped = this.bracketType === 'propped';

    // Hanging: vertical arm extends upward above board
    // Propped: vertical arm extends downward below horizontal arm
    // Floating: no vertical arm (hidden rods)
    let verticalHeight;
    if (isFloating) {
      verticalHeight = 0;
    } else if (isPropped) {
      verticalHeight = armLength * 0.85;
    } else {
      verticalHeight = (boardTop - (boardBottom - metalThickness)) + widthMm * 0.35;
    }

    const lipHeight = isFloating ? 0 : thickMm * 0.6;

    const originY = isFloating
      ? boardBottom + thickMm / 2   // rods centered at board mid-height
      : boardBottom - metalThickness;
    const originZ = wallFace;

    // Bracket count from length (same logic for all 3 types: hanging, propped, floating)
    const bracketCount = this.getBracketCount(lengthMm);
    const inset = lengthMm * 0.15;

    for (let i = 0; i < bracketCount; i++) {
      const bracket = this.createBracketGroup({
        metalThickness, metalWidth, verticalHeight, armLength, lipHeight,
        boardThickness: thickMm
      });

      let xPos;
      if (bracketCount === 2) {
        xPos = i === 0 ? -lengthMm / 2 + inset : lengthMm / 2 - inset;
      } else {
        // Evenly distribute brackets across the board length
        const usableLength = lengthMm - inset * 2;
        const spacing = usableLength / (bracketCount - 1);
        xPos = -lengthMm / 2 + inset + spacing * i;
      }

      bracket.position.set(xPos, originY, originZ);
      this.scene.add(bracket);
      this.brackets.push(bracket);
    }

    this.bracketsVisible = true;
  }

  removeBrackets() {
    if (!this.brackets) return;
    this.brackets.forEach(group => {
      group.traverse(child => {
        if (child.isMesh) {
          child.geometry.dispose();
          child.material.dispose();
        }
      });
      this.scene.remove(group);
    });
    this.brackets = [];
    this.bracketsVisible = false;
  }

  setBracketColor(color) {
    const c = color instanceof THREE.Color ? color : new THREE.Color(color);
    this.bracketColor = c;
    this.brackets.forEach(group => {
      group.traverse(child => {
        if (child.isMesh) {
          child.material.color.set(c);
        }
      });
    });
  }

  onResize() {
    const container = this.querySelector('#canvas-container');
    if (!container) return;

    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);

    // After resize, ensure proper fit on mobile
    if (this.isMobile()) {
      this.fitCameraToObject(this.board, 1.6);
    }
  }

  animate() {
    const loop = () => {
      this.animationId = requestAnimationFrame(loop);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    loop();
  }

  cleanup() {
    // Cancel animation frame
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    // Dispose of geometries
    if (this.board && this.board.geometry) {
      this.board.geometry.dispose();
    }
    if (this.wall && this.wall.geometry) {
      this.wall.geometry.dispose();
    }

    // Dispose of materials
    if (this.board && this.board.material) {
      if (this.board.material.map) this.board.material.map.dispose();
      this.board.material.dispose();
    }
    if (this.wall && this.wall.material) {
      if (Array.isArray(this.wall.material)) {
        this.wall.material.forEach(mat => {
          if (mat.map) mat.map.dispose();
          if (mat.aoMap) mat.aoMap.dispose();
          if (mat.normalMap) mat.normalMap.dispose();
          if (mat.displacementMap) mat.displacementMap.dispose();
          mat.dispose();
        });
      } else {
        if (this.wall.material.map) this.wall.material.map.dispose();
        this.wall.material.dispose();
      }
    }

    // Dispose of renderer and context
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }

    // Dispose of controls
    if (this.controls) {
      this.controls.dispose();
    }

    // Clear scene
    if (this.scene) {
      this.scene.clear();
    }

    // Remove event listeners
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize);
    }
    if (this.boundOnLengthChange) {
      document.removeEventListener('length-changed', this.boundOnLengthChange);
    }
    if (this.boundOnWidthChange) {
      document.removeEventListener('width-prime-changed', this.boundOnWidthChange);
    }
    if (this.boundOnAddonUpdate) {
      document.removeEventListener('addon:update-gallery', this.boundOnAddonUpdate);
    }
    if (this.boundOnBracketTypeClick) {
      document.querySelectorAll('[data-bracket-item]').forEach(item => {
        item.removeEventListener('click', this.boundOnBracketTypeClick);
      });
    }

    // Dispose brackets
    this.removeBrackets();
  }

  disconnectedCallback() {
    this.cleanup();
    this.isInitialized = false;
  }

  // Helpers
  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  fitCameraToObject(object, offset = 1.2) {
    if (!object || !this.camera || !this.controls) return;

    const box = new THREE.Box3().setFromObject(object);
    const sphere = box.getBoundingSphere(new THREE.Sphere());

    const vFOV = THREE.MathUtils.degToRad(this.camera.fov);
    let distance = sphere.radius / Math.sin(vFOV / 2);

    // Respect control bounds
    distance = Math.max(distance * offset, this.controls.minDistance);
    distance = Math.min(distance, this.controls.maxDistance);

    // Move camera along current view direction to keep angle
    const dir = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();

    this.controls.target.copy(sphere.center);
    this.camera.position.copy(dir.multiplyScalar(distance).add(sphere.center));
    this.camera.lookAt(sphere.center);
    this.controls.update();
  }
}

customElements.define('build-your-board-model', BuildYourBoardModel);


document.querySelectorAll('[data-tippy-content]').forEach((el) => {
  tippy(el, {
    placement: 'top',
    animation: 'perspective-extreme',
    duration: 0,
    arrow: true,
    allowHTML: true,
    theme: el.hasAttribute('coming-soon') ? 'coming-soon' : 'dark',
  });
});
